import React, { Fragment, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";

import { Image } from "@onair/minimal-ui/image";
import { toast } from "@onair/minimal-ui/snackbar";
import { Scrollbar } from "@onair/minimal-ui/scrollbar";
import { GetResourceResponseDto } from "@onair/repositories";
import { useDebounce } from "@onair/minimal-ui/hooks/use-debounce";

// import {
// 	useDeleteResourceMutation,
// 	useGetResourceLinkQuery,
// 	useResourceSearchLinkListMutation,
// } from "#src/operations";

import {
	useDeleteResourceMutation,
	useGetResourceLinkQuery,
	useResourceSearchLinkListMutation,
} from "@onair/operations";
import LinkResourceItem from "./link-resource-item";

const CreateAndUpdateLink = dynamic(() => import("./create-and-update-link"));

interface ILinkTabProps {
	rootLibraryId: string;
}

const LinkTab = (props: ILinkTabProps) => {
	const { rootLibraryId } = props;

	const [openDialog, setOpenDialog] = useState<boolean>(false);

	const [editLink, setEditLink] = useState<{
		id: string | null;
		fileName: string | null;
		publicUrl: string | null;
	}>({
		id: null,
		fileName: null,
		publicUrl: null,
	});

	const [searchQuery, setSearchQuery] = useState<string>("");
	const [showClearIcon, setShowClearIcon] = useState<boolean>(false);

	const debouncedQuery = useDebounce(searchQuery, 1000);

	const {
		data,
		isLoading,
		refetch: refetchGetResourceLink,
	} = useGetResourceLinkQuery(rootLibraryId);

	const { data: searchData, mutateAsync: searchResourceLink } =
		useResourceSearchLinkListMutation();

	const { mutateAsync: deleteResource } = useDeleteResourceMutation({
		onSuccess: async () => await refetchGetResourceLink(),
		onError: async () => toast.error("Có lỗi xảy ra. Vui lòng thử lại sau"),
	});

	useEffect(() => {
		if (debouncedQuery) {
			searchResourceLink(debouncedQuery).then();
		} else {
			refetchGetResourceLink().then();
		}
	}, [debouncedQuery]);

	const handleOpenDialog = () => {
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchQuery(value);
		setShowClearIcon(!!value.length);
	};

	const handleClearSearchQuery = async () => {
		setSearchQuery("");
		setShowClearIcon(false);

		await refetchGetResourceLink();
	};

	const renderItems = (linkResources: GetResourceResponseDto[]) => {
		if (isLoading) {
			return (
				<Grid item xs={24}>
					<Box display="flex" justifyContent="center" alignItems="center">
						<CircularProgress />
					</Box>
				</Grid>
			);
		}

		if (!linkResources.length) {
			return (
				<Grid item xs={24}>
					<Box display="flex" justifyContent="center" alignItems="center">
						<Stack direction="column" spacing={4}>
							<Image
								borderRadius={2}
								width="120"
								height="120"
								src="/assets/images/online-course/empty-link.png"
								alt="Published Success Image"
								className="object-cover h-full"
							/>
							<Typography className="text-center">
								Lưu các liên kết của bạn
							</Typography>
						</Stack>
					</Box>
				</Grid>
			);
		}

		return (
			<Fragment>
				{linkResources.map((resource) => (
					<LinkResourceItem
						key={resource.id}
						resource={resource}
						onDelete={deleteResource}
					/>
				))}
			</Fragment>
		);
	};

	return (
		<Box>
			<CreateAndUpdateLink
				rootLibraryId={rootLibraryId}
				open={openDialog}
				onClose={handleCloseDialog}
				refetch={refetchGetResourceLink}
			/>

			<Stack spacing={2} direction="row" justifyContent="space-between">
				<Stack direction={{ sm: "row", xs: "column" }} flex={1} spacing={1}>
					<TextField
						placeholder="Tìm kiếm"
						size="small"
						variant="outlined"
						value={searchQuery}
						onChange={handleSearchChange}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
							endAdornment: showClearIcon && (
								<InputAdornment position="end">
									<CloseIcon
										onClick={handleClearSearchQuery}
										style={{ cursor: "pointer" }}
									/>
								</InputAdornment>
							),
						}}
					/>

					<Button variant="outlined" onClick={handleOpenDialog}>
						Tạo đường dẫn
					</Button>
				</Stack>
			</Stack>

			<Box className="mt-4">
				<Scrollbar fillContent>
					<Grid container spacing={2} columns={24}>
						{debouncedQuery && searchData
							? renderItems(searchData)
							: renderItems(data || [])}
					</Grid>
				</Scrollbar>
			</Box>
		</Box>
	);
};

export default LinkTab;
